import { brandPalette } from "../../../constants/brandPalette";
import { Badge, Box, Button, Flex, HStack, Spinner, Text, VStack } from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import { MdCheck, MdClose, MdExpandLess, MdExpandMore } from "react-icons/md";

const STATUS_META = {
  building: { label: "Building PDF", color: "orange.700", bg: "rgba(237, 137, 54, 0.15)" },
  uploading: { label: "Uploading", color: brandPalette.primary, bg: "rgba(91, 13, 22, 0.1)" },
  done: { label: "Sent", color: "green.700", bg: "rgba(72, 187, 120, 0.15)" },
  error: { label: "Failed", color: "red.600", bg: "rgba(229, 62, 62, 0.12)" },
};

function studentLabel(job) {
  return job?.studentLabel || `Submission #${job?.id}`;
}

const BackgroundUploadsPanel = ({ jobs, onRetry, onClearDone }) => {
  const items = Array.isArray(jobs) ? jobs : [];
  const [expanded, setExpanded] = useState(true);

  const activeCount = useMemo(
    () => items.filter((job) => job.status === "building" || job.status === "uploading").length,
    [items]
  );
  const doneCount = useMemo(
    () => items.filter((job) => job.status === "done").length,
    [items]
  );
  const errorCount = useMemo(
    () => items.filter((job) => job.status === "error").length,
    [items]
  );

  if (!items.length) return null;

  const summary = activeCount
    ? `${activeCount} uploading`
    : errorCount
      ? `${errorCount} failed`
      : `${doneCount} sent`;

  return (
    <Box
      position="fixed"
      left={{ base: 3, md: 6 }}
      top={{ base: 4, md: 6 }}
      zIndex={1800}
      w={{ base: "calc(100% - 24px)", sm: "320px" }}
      maxW="320px"
      bg="var(--surface-raised)"
      borderWidth="1px"
      borderColor="var(--border-subtle)"
      borderRadius="xl"
      boxShadow="0 16px 40px rgba(45, 42, 38, 0.18)"
      overflow="hidden"
    >
      <Flex
        as="button"
        type="button"
        w="100%"
        align="center"
        justify="space-between"
        gap={3}
        px={4}
        py={3}
        onClick={() => setExpanded((open) => !open)}
        bg="var(--surface-muted)"
      >
        <Box textAlign="left">
          <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" textTransform="uppercase">
            Correction uploads
          </Text>
          <Text fontSize="sm" fontWeight="800" color="var(--text-primary)">
            {summary}
          </Text>
        </Box>
        <HStack spacing={2}>
          {activeCount ? <Spinner size="sm" color={brandPalette.primary} /> : null}
          {expanded ? <MdExpandLess size={22} /> : <MdExpandMore size={22} />}
        </HStack>
      </Flex>

      {expanded ? (
        <Box px={3} py={3}>
          <VStack align="stretch" spacing={2} maxH="280px" overflowY="auto">
            {items.map((job) => {
              const meta = STATUS_META[job.status] || STATUS_META.uploading;
              const busy = job.status === "building" || job.status === "uploading";
              return (
                <Flex
                  key={job.id}
                  align="flex-start"
                  justify="space-between"
                  gap={3}
                  p={3}
                  borderWidth="1px"
                  borderColor="var(--border-subtle)"
                  borderRadius="lg"
                  bg="var(--surface-muted)"
                >
                  <Box minW={0} flex="1">
                    <Text fontSize="sm" fontWeight="700" color="var(--text-primary)" noOfLines={1}>
                      {studentLabel(job)}
                    </Text>
                    {job.error ? (
                      <Text fontSize="xs" color="red.500" mt={1} noOfLines={2}>
                        {job.error}
                      </Text>
                    ) : null}
                  </Box>
                  <HStack spacing={2} flexShrink={0}>
                    {busy ? (
                      <Spinner size="sm" color={brandPalette.primary} />
                    ) : job.status === "done" ? (
                      <MdCheck color="#38A169" size={18} />
                    ) : job.status === "error" ? (
                      <MdClose color="#E53E3E" size={18} />
                    ) : null}
                    <Badge
                      px={2}
                      py={0.5}
                      borderRadius="md"
                      fontWeight="700"
                      fontSize="xs"
                      bg={meta.bg}
                      color={meta.color}
                    >
                      {meta.label}
                    </Badge>
                    {job.status === "error" && onRetry ? (
                      <Button
                        size="xs"
                        variant="ghost"
                        fontWeight="800"
                        color={brandPalette.primary}
                        onClick={() => onRetry(job)}
                      >
                        Retry
                      </Button>
                    ) : null}
                  </HStack>
                </Flex>
              );
            })}
          </VStack>
          {doneCount || errorCount ? (
            <Flex justify="flex-end" mt={3}>
              <Button
                size="xs"
                variant="ghost"
                fontWeight="700"
                color="var(--text-muted)"
                onClick={onClearDone}
              >
                Clear finished
              </Button>
            </Flex>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

export default BackgroundUploadsPanel;
